import { useEffect, useState } from "react";
import { useModals } from "../../store/dialogs/hooks";
import { useNoiseFilter } from "../../store/noiseFilter/hooks";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material";
import { isEqual } from "lodash";
import { NumericInput } from "../NumericInput";
import { Transition } from "../../pages/app/utils/animation";

export const SngSettingDialog = ({ onRun }) => {
  const {
    noiseStart,
    noiseEnd,
    fftLength,
    noiseCancelRatio,
    fdMaskAveraging,
    tdMaskAveraging,
    nStdThreshold,
    reset: noiseFilterReset,
    updateNoiseFilter,
  } = useNoiseFilter();
  const { closeSngFilterDialog, showSngFilterDialog } = useModals();

  const [initialState, setInitialState] = useState({});

  const handleClose = () => closeSngFilterDialog();

  const handleRunFilter = () => {
    onRun && onRun();
    handleClose();
  };

  const isUpdated = isEqual(
    initialState,
    Object.entries({
      noiseStart,
      noiseEnd,
      fftLength,
      noiseCancelRatio,
      fdMaskAveraging,
      tdMaskAveraging,
      nStdThreshold,
    }).reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {}),
  );

  useEffect(() => {
    setInitialState({
      noiseStart,
      noiseEnd,
      fftLength,
      noiseCancelRatio,
      fdMaskAveraging,
      tdMaskAveraging,
      nStdThreshold,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog
      aria-hidden={!showSngFilterDialog}
      onClose={handleClose}
      open={showSngFilterDialog}
      closeAfterTransition={false}
      slots={{
        transition: Transition,
      }}
    >
      <DialogTitle>Noise Gate (Spectral)</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ mb: 3 }}>
          Please edit the settings below as needed to run the noise filter. If
          you are unsure of the settings, try using the default values.
        </DialogContentText>
        <Stack gap={4}>
          <Box display="flex" gap={2}>
            <NumericInput
              label="Noise Sample Start"
              decimal={3}
              value={noiseStart}
              caption="The starting point in audio file for noise reference"
              onChanged={(value) =>
                updateNoiseFilter({ noiseStart: Number(value) })
              }
              right="s"
            />
            <NumericInput
              label="Noise Sample Stop"
              decimal={3}
              value={noiseEnd}
              caption="The stopping point in audio file for noise reference"
              onChanged={(value) =>
                updateNoiseFilter({ noiseEnd: Number(value) })
              }
              right="s"
            />
          </Box>
          <Box display="flex" gap={2}>
            <NumericInput
              label="FFT Length"
              value={fftLength}
              caption="The size of FFT to perform  (must be a power of 2)"
              onChanged={(value) =>
                updateNoiseFilter({ fftLength: Number(value) })
              }
            />
            <NumericInput
              label="Noise Cancellation Ratio"
              value={noiseCancelRatio}
              decimal={1}
              step={0.05}
              min={0}
              max={1}
              caption="The noise cancellation ratio (0.0 to 1.0)"
              onChanged={(value) => {
                if (value < 0) value = 0;
                if (value > 1) value = 1;
                updateNoiseFilter({ noiseCancelRatio: Number(value) });
              }}
            />
            <NumericInput
              label="Std Threshold"
              value={nStdThreshold}
              decimal={1}
              step={0.05}
              caption="The standard deviation above mean for threshold"
              onChanged={(value) =>
                updateNoiseFilter({ nStdThreshold: Number(value) })
              }
            />
          </Box>
          <Box display="flex" gap={2}>
            <NumericInput
              label="Mask Smoothing (Frequency)"
              value={fdMaskAveraging}
              caption="The mask smoothing frequency range in Hz"
              onChanged={(value) =>
                updateNoiseFilter({ fdMaskAveraging: Number(value) })
              }
            />
            <NumericInput
              label="Mask Smoothing (Time)"
              value={tdMaskAveraging}
              caption="The mask smoothing time range in milliseconds"
              onChanged={(value) =>
                updateNoiseFilter({ tdMaskAveraging: Number(value) })
              }
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          disabled={isUpdated}
          onClick={() => noiseFilterReset()}
          variant="outlined"
        >
          Reset to Default
        </Button>
        <Button onClick={handleRunFilter} variant="contained">
          Run
        </Button>
      </DialogActions>
    </Dialog>
  );
};
